import { ClipboardEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';

import { Combobox } from '@base-ui/react/combobox';
import type { BaseUIEvent } from '@base-ui/react/types';

import { useEvent } from '@hooks/use-event';

import { blockEscapeSelectionClear, isUserInputEdit } from './base-ui-event';

type UseEchoedSelectionInputArgs = {
  hasSelection: boolean;
  isClearable: boolean;
  placeholder: string;
  selectedText: string | null;
  overlayContent: ReactNode;
};

export type EchoedSelectionInput = ReturnType<typeof useEchoedSelectionInput>;

// While the user edits the query text the input shows what they type; the
// rest of the time it echoes the committed selection underneath a
// ValueOverlay that renders the selection's label. Owns the open and input
// state that distinction depends on.
export function useEchoedSelectionInput({
  hasSelection,
  isClearable,
  placeholder,
  selectedText,
  overlayContent,
}: UseEchoedSelectionInputArgs) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(false);
  const isTypedQueryRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // The committed selection renders through the ValueOverlay on top of the
  // (text-transparent) input: a focused <input> never ellipsizes its text, and
  // a rich (non-string) label can't be typed into one at all. The overlay
  // hides while the user is typing a query so their text is visible.
  const overlay = overlayContent != null && !isUserEditing ? overlayContent : null;
  // A string label's overlay repeats the echoed input text glyph for glyph, so
  // the caret still sits on visible text. A rich label's echo is unrelated
  // text under unrelated content, so the caret would float mid-label.
  const isRichOverlay = overlay != null && typeof overlay !== 'string';

  // What the sizer mirrors so the control's intrinsic width tracks the visible
  // content. The overlay sizes the cell itself while it shows.
  const sizerText = isUserEditing
    ? inputValue
    : overlay != null
      ? ''
      : (selectedText ?? placeholder);

  const endEditing = () => {
    setIsUserEditing(false);
    isTypedQueryRef.current = false;
  };

  const handleOpenChange = useEvent((open: boolean) => {
    if (!open) endEditing();
    setIsOpen(open);
  });

  // base-ui writes a changed selection's label into the input even mid-query,
  // which would filter the list by that label; closing the popup echoes it.
  const isSelectionEchoOverQuery = (details: Combobox.Root.ChangeEventDetails) =>
    details.reason === 'none' && isOpen && isUserEditing;

  const handleInputValueChange = useEvent(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      if (isSelectionEchoOverQuery(details)) {
        details.cancel();
        return false;
      }
      setInputValue(next);
      if (!isUserInputEdit(details.reason)) {
        endEditing();
        return false;
      }
      const isDeletion =
        details.event instanceof InputEvent && details.event.inputType.startsWith('delete');
      if (next !== '' && !isDeletion) isTypedQueryRef.current = true;
      setIsUserEditing(true);
      return true;
    },
  );

  // An editable base-ui combobox reports a null selection whenever its input
  // is emptied. A non-clearable select keeps its value, and so does any select
  // whose emptied text was a query the user typed or pasted rather than the
  // echoed label deleted in place; closing the popup echoes the label again.
  const keepsSelectionOnEmptiedInput = (reason: Combobox.Root.ChangeEventReason) =>
    !isClearable || (reason === 'input-clear' && isTypedQueryRef.current);

  // base-ui keeps focus in the input after a pick, which on a touch screen
  // leaves the on-screen keyboard covering the page. A keyboard pick keeps
  // focus, since the user is still typing on a keyboard; a click with a
  // `detail` of 0 was synthesised from a key.
  const blurAfterPickOnTouchScreen = (event: Event | undefined) => {
    const isKeyboardPick =
      event instanceof globalThis.KeyboardEvent ||
      (event instanceof MouseEvent && event.detail === 0);
    if (window.matchMedia?.('(pointer: coarse)').matches && !isKeyboardPick) {
      inputRef.current?.blur();
    }
  };

  const isEchoShown = !isUserEditing && hasSelection;

  // The input echoes the committed selection's label, so a keystroke would
  // append to it and query for text that matches nothing. Select the echoed
  // text so the first keystroke (or paste, below) starts a fresh query. A dead
  // key or an input method composes its letter instead, and selecting on its
  // keydown puts the selection in place before the composition starts.
  const onKeyDown = (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => {
    blockEscapeSelectionClear(event, isOpen);
    const startsComposition = event.key === 'Dead' || event.key === 'Process';
    const typesCharacter =
      event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (isEchoShown && (startsComposition || typesCharacter)) event.currentTarget.select();
  };

  // Text can also arrive with no keystroke to select on: the emoji picker,
  // dictation, an Option-key letter, Android's keyboard. WebKit ignores a
  // selection made during beforeinput, so a cancelable insertion is replayed
  // over the selected echo instead.
  const handleBeforeInput = useEvent((event: InputEvent) => {
    const input = inputRef.current;
    if (!input || !isEchoShown || !event.inputType.startsWith('insert')) return;
    if (input.selectionStart === 0 && input.selectionEnd === input.value.length) return;
    input.select();
    if (!event.cancelable || event.data === null) return;
    event.preventDefault();
    document.execCommand('insertText', false, event.data);
  });

  useEffect(() => {
    const input = inputRef.current;
    input?.addEventListener('beforeinput', handleBeforeInput);
    return () => input?.removeEventListener('beforeinput', handleBeforeInput);
  }, [handleBeforeInput]);

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (isEchoShown && event.clipboardData.getData('text')) event.currentTarget.select();
  };

  return {
    inputRef,
    query: isUserEditing ? inputValue : '',
    isOpen,
    setIsOpen,
    handleOpenChange,
    handleInputValueChange,
    keepsSelectionOnEmptiedInput,
    endEditing,
    blurAfterPickOnTouchScreen,
    overlay,
    isRichOverlay,
    sizerText,
    onKeyDown,
    onPaste,
  };
}
